import * as React from 'react'
import { useGetPlcSyncStatusQuery, type PlcSyncState } from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../../components/ui/badge'

/** Terminal pull-sync states — stop polling once one is reached. */
const TERMINAL_STATES: ReadonlySet<PlcSyncState> = new Set([
  'no_change',
  'applied',
  'failed',
  'cancelled'
])

interface StatusView {
  label: string
  className: string
}

/**
 * Operator-facing view for each pull-sync state. `diff_ready` is a transient
 * hand-off (the machine reported a diff; the apply executor picks it up), shown
 * as "Diff ready" so a stalled apply is visible.
 */
function viewForState(state: PlcSyncState): StatusView {
  switch (state) {
    case 'queued':
      return { label: 'Queued', className: 'bg-st-info-bg text-st-info' }
    case 'claimed':
      return { label: 'Claimed', className: 'bg-st-warn-bg text-st-warn' }
    case 'diff_ready':
      return { label: 'Diff ready', className: 'bg-st-warn-bg text-st-warn' }
    case 'applying':
      return { label: 'Applying', className: 'bg-st-warn-bg text-st-warn' }
    case 'no_change':
      return { label: 'No sync needed', className: 'bg-st-idle-bg text-st-idle' }
    case 'applied':
      return { label: 'Updated', className: 'bg-st-run-bg text-st-run' }
    case 'cancelled':
      return { label: 'Cancelled', className: 'bg-st-idle-bg text-st-idle' }
    case 'failed':
      return { label: 'Failed', className: 'bg-st-down-bg text-st-down' }
    default:
      return { label: state, className: 'bg-st-idle-bg text-st-idle' }
  }
}

/**
 * One controller's pull-sync status, polled from the BFF until terminal. Sync
 * requests publish no wake-up, so this stays a deliberate poll.
 */
function PlcSyncRow({ jobId }: { jobId: string }) {
  const [terminal, setTerminal] = React.useState(false)
  const { data, isLoading, isError } = useGetPlcSyncStatusQuery(jobId, {
    pollingInterval: terminal ? 0 : 3000
  })

  // Latch terminal so polling stops once the request settles.
  React.useEffect(() => {
    if (data && TERMINAL_STATES.has(data.state)) setTerminal(true)
  }, [data])

  const controllerName = data?.controller_name || jobId.slice(0, 8)
  const view = data ? viewForState(data.state) : null

  return (
    <div className="flex flex-col gap-1 rounded-md border border-line px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate text-13 font-medium text-ink">
          {controllerName}
        </span>
        {view ? (
          <Badge variant="secondary" className={`border-transparent ${view.className}`}>
            {view.label}
          </Badge>
        ) : isLoading ? (
          <Badge variant="secondary" className="border-transparent bg-st-info-bg text-st-info">
            Loading…
          </Badge>
        ) : isError ? (
          <Badge variant="secondary" className="border-transparent bg-st-down-bg text-st-down">
            Unavailable
          </Badge>
        ) : null}
      </div>
      {data?.error ? <p className="text-xs text-st-down">{data.error}</p> : null}
    </div>
  )
}

/**
 * Per-controller pull-sync status for an Update-PLC launch. Each `plc_update`
 * ingest job owns one durable pull-sync request; this polls
 * `GET /plc-sync/jobs/:jobId/status` for each until it settles.
 */
export function PlcUpdateStatusPanel({ jobIds }: { jobIds: string[] }) {
  if (jobIds.length === 0) return null
  return (
    <div className="flex flex-col gap-2 border-t border-line px-3.5 py-3">
      {jobIds.map((jobId) => (
        <PlcSyncRow key={jobId} jobId={jobId} />
      ))}
    </div>
  )
}
